// OP-3605 — Contratos e termos (ESL1): o instrumento de responsabilidade,
// por praça, status e vigência. Não confundir com Remessas: lá é cada envio e
// o seu retorno; aqui é quem responde pelo equipamento e até quando.
import { FC, useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useEstoqueLocais, useEstoqueVinculos } from '../../../../hooks/estoque'
import { NaturezaVinculo, StatusVinculo, VinculoRow, VinculosFilters } from '../../../../types/estoque'
import {
  BarraChips, BD, Button, CaixaLista, Card, Chip, Field, Input, ListaCartoes, PageSubtitle, PageTitle,
  Select, SoDesktop, Spinner, StatusBadge, Table, TabelaWrap,
} from '../../styles'
import { fmtDate } from '../../statusUi'
import { Cartao } from '../Cartao'
import { Filtros } from '../Filtros'
import { plural } from '../formato'
import { hojeISO } from '../Solicitar/form'
import { NATUREZA_LABEL, STATUS_VINCULO_UI, atrasoRetorno, nomeDaPraca, pendenciasDePatrimonio, statusDoVinculoUi, vencidoComPeca, vigenciaDe } from '../Vinculo/regras'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'

const STATUS: StatusVinculo[] = ['VIGENTE', 'ENCERRADO', 'CANCELADO']
const NATUREZAS: NaturezaVinculo[] = ['CONTRATO', 'TERMO']
const NATUREZA_BADGE: Record<NaturezaVinculo, { bg: string; color: string; label: string }> = {
  CONTRATO: { bg: '#E0E7FF', color: '#3730A3', label: 'Contrato' },
  TERMO: { bg: '#FEF3C7', color: '#92400E', label: 'Termo' },
}
const COLS = 7

/** A vigência do INSTRUMENTO como pílula: vencida com peça é a linha que dói. */
export const VigenciaBadge: FC<{ v: Pick<VinculoRow, 'status' | 'vigenciaFim' | 'pecasFora'>; hoje: string }> = ({ v, hoje }) => {
  const vig = vigenciaDe(v.vigenciaFim, hoje)
  if (vig.tipo === 'A_DEFINIR') return <StatusBadge $bg="#F3F4F6" $color="#374151">a definir</StatusBadge>
  if (vig.tipo === 'VENCIDA') {
    return vencidoComPeca(v, hoje)
      ? <StatusBadge $bg="#FEE2E2" $color="#991B1B">vencida há {vig.dias}d · equipamento fora</StatusBadge>
      : <StatusBadge $bg="#FEF3C7" $color="#92400E">vencida</StatusBadge>
  }
  if (vig.tipo === 'VENCE_EM_BREVE') return <StatusBadge $bg="#FEF3C7" $color="#92400E">vence em {vig.dias}d</StatusBadge>
  return <StatusBadge $bg="#D1FAE5" $color="#065F46">{vig.dias}d</StatusBadge>
}

/** Peça extraviada e baixa pendente: pendências de patrimônio, em cor de atenção. */
export const PendenciasBadges: FC<{ v: Pick<VinculoRow, 'pecasExtraviadas' | 'baixasPendentes'>; alinhar?: 'flex-start' | 'flex-end' }> = ({ v, alinhar = 'flex-end' }) => {
  const itens = pendenciasDePatrimonio(v)
  if (!itens.length) return null
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: alinhar, marginTop: 4 }}>
      {itens.map((p) => <StatusBadge key={p.texto} $bg="#FEF3C7" $color="#92400E">{p.texto}</StatusBadge>)}
    </div>
  )
}

export const EstoqueVinculos: FC = () => {
  const navigate = useNavigate()
  const hoje = useMemo(hojeISO, [])
  const [draft, setDraft] = useState<VinculosFilters>({ page: 1 })
  const [applied, setApplied] = useState<VinculosFilters>({ page: 1 })
  const { rows, total, page, lastPage, isLoading, isError, isUnauthorized } = useEstoqueVinculos(applied)
  const { locais } = useEstoqueLocais()
  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const aplicar = () => setApplied({ ...draft, page: 1 })
  const limpar = () => {
    setDraft({ page: 1 })
    setApplied({ page: 1 })
  }
  const escolherStatus = (status: StatusVinculo | '') => {
    setDraft((d) => ({ ...d, status, page: 1 }))
    setApplied((a) => ({ ...a, status, page: 1 }))
  }
  const ativos = [applied.busca, applied.pracaId, applied.natureza]
    .filter((v) => v != null && String(v) !== '').length
  const contagem = isLoading ? 'Carregando…' : plural(total, 'contrato', 'contratos')

  const linha = (v: VinculoRow) => {
    const ui = statusDoVinculoUi(v, hoje)
    const atraso = atrasoRetorno(v.proximoRetorno, v.pecasFora, hoje)
    return (
      <tr key={v.id} onClick={() => navigate(`/interno/estoque/vinculos/${v.id}`)} style={{ cursor: 'pointer' }}>
        <td style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v.codigo}</span>
          <StatusBadge $bg={NATUREZA_BADGE[v.natureza]?.bg ?? '#F3F4F6'} $color={NATUREZA_BADGE[v.natureza]?.color ?? '#374151'} style={{ marginLeft: 8 }} title={NATUREZA_LABEL[v.natureza] ?? v.natureza}>
            {NATUREZA_BADGE[v.natureza]?.label ?? v.natureza}
          </StatusBadge>
        </td>
        <td><b>{v.detentorNome}</b><div style={{ fontSize: 12, color: BD.grayText }}>{nomeDaPraca(locais, v.pracaId)}</div></td>
        <td style={{ textAlign: 'right' }}>
          {Number(v.pecasFora)}{Number(v.consumoEnviado) > 0 && <div style={{ fontSize: 11, color: BD.grayText }}>+{Number(v.consumoEnviado)} consumo</div>}
          <PendenciasBadges v={v} />
        </td>
        <td style={{ fontSize: 13 }}>{fmtDate(v.proximoRetorno)}</td>
        <td>
          {atraso > 0
            ? <StatusBadge $bg="#FEE2E2" $color="#991B1B">{Number(v.pecasFora)} equip. · {atraso}d</StatusBadge>
            : Number(v.pecasFora) > 0 ? <StatusBadge $bg="#D1FAE5" $color="#065F46">no prazo</StatusBadge> : <span style={{ color: BD.grayText }}>—</span>}
        </td>
        <td><VigenciaBadge v={v} hoje={hoje} /></td>
        <td><StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge></td>
      </tr>
    )
  }

  const cartao = (v: VinculoRow) => {
    const ui = STATUS_VINCULO_UI[v.status] ?? { bg: '#F3F4F6', color: '#374151', label: v.status }
    const natureza = NATUREZA_BADGE[v.natureza]
    const atraso = atrasoRetorno(v.proximoRetorno, v.pecasFora, hoje)
    const fora = Number(v.pecasFora)
    return (
      <Cartao
        key={v.id}
        to={`/interno/estoque/vinculos/${v.id}`}
        codigo={v.codigo}
        badge={natureza ?? ui}
        titulo={v.detentorNome}
        esmaecido={v.status !== 'VIGENTE'}
        destaque={atraso > 0 || vencidoComPeca(v, hoje)}
        meta={(
          <>
            {nomeDaPraca(locais, v.pracaId)} · {plural(fora, 'equipamento fora', 'equipamentos fora')}
            {Number(v.consumoEnviado) > 0 ? ` + ${Number(v.consumoEnviado)} un. de consumo` : ''}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
              <VigenciaBadge v={v} hoje={hoje} />
              {v.status !== 'VIGENTE' && <StatusBadge $bg={ui.bg} $color={ui.color}>{ui.label}</StatusBadge>}
            </div>
            <PendenciasBadges v={v} alinhar="flex-start" />
          </>
        )}
        rodape={atraso > 0 ? (
          <span style={{ color: BD.redDark, fontWeight: 600 }}>Retorno atrasado há {atraso}d</span>
        ) : (
          <span style={{ color: BD.grayText }}>
            {fora > 0 ? `Próximo retorno ${fmtDate(v.proximoRetorno)}` : 'Nada em campo'}
          </span>
        )}
      />
    )
  }

  return (
    <>
      <PageTitle>Contratos e termos</PageTitle>
      <PageSubtitle>
        Quem responde pelo equipamento que está fora, e até quando. Um contrato cobre várias remessas; eles nascem na expedição do pedido.
        Vencido com equipamento no produtor é a linha que dói; vencido sem equipamento é só cadastro a encerrar — salvo peça extraviada com baixa pendente.
      </PageSubtitle>
      <BarraChips role="group" aria-label="Status do contrato">
        <Chip type="button" $ativo={!applied.status} onClick={() => escolherStatus('')}>Todos</Chip>
        {STATUS.map((s) => (
          <Chip key={s} type="button" $ativo={applied.status === s} onClick={() => escolherStatus(s)}>
            {STATUS_VINCULO_UI[s].label}
          </Chip>
        ))}
      </BarraChips>

      <Filtros aoAplicar={aplicar} aoLimpar={limpar} ativos={ativos} resumo={contagem} confirmar="Aplicar filtros">
        <Field>
          <label>Código ou detentor</label>
          <Input value={draft.busca ?? ''} onChange={(e) => setDraft((d) => ({ ...d, busca: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && aplicar()} />
        </Field>
        <Field>
          <label>Praça</label>
          <Select value={draft.pracaId ?? ''} onChange={(e) => setDraft((d) => ({ ...d, pracaId: e.target.value }))}>
            <option value="">todas</option>
            {locais.filter((l) => l.noEscopo && l.pracaId != null)
              .map((l) => <option key={l.id} value={l.pracaId as number}>{l.nome}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Status</label>
          <Select value={draft.status ?? ''} onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value as StatusVinculo | '' }))}>
            <option value="">todos</option>
            {STATUS.map((s) => <option key={s} value={s}>{STATUS_VINCULO_UI[s].label}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Instrumento</label>
          <Select value={draft.natureza ?? ''} onChange={(e) => setDraft((d) => ({ ...d, natureza: e.target.value as NaturezaVinculo | '' }))}>
            <option value="">todos</option>
            {NATUREZAS.map((n) => <option key={n} value={n}>{NATUREZA_LABEL[n]}</option>)}
          </Select>
        </Field>
      </Filtros>
      <CaixaLista>
        <SoDesktop>
          <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{contagem}</div>
        </SoDesktop>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Código</th><th>Detentor</th><th style={{ textAlign: 'right' }}>Equip. fora</th><th>Próximo retorno</th><th>Retorno</th><th>Vigência</th><th>Status</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>Não foi possível carregar os contratos.</td></tr>}
              {!isLoading && !isError && rows.length === 0 && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>
                  Nenhum contrato com os filtros atuais. Eles nascem na expedição: ao expedir um pedido, o sistema anexa o contrato que o detentor já tem, ou cria um.
                </td></tr>
              )}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && <Card style={{ padding: 20, color: BD.red }}>Não foi possível carregar os contratos.</Card>}
          {!isLoading && !isError && rows.length === 0 && (
            <Card style={{ padding: 20, color: BD.grayText }}>
              Nenhum contrato com os filtros atuais. Eles nascem na expedição: ao expedir um pedido, o sistema anexa o contrato que o detentor já tem, ou cria um.
            </Card>
          )}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>

        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={(page ?? 1) <= 1} onClick={() => setApplied((a) => ({ ...a, page: (page ?? 1) - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {page ?? 1}</span>
          <Button $variant="secondary" disabled={!!lastPage} onClick={() => setApplied((a) => ({ ...a, page: (page ?? 1) + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>
    </>
  )
}

export default EstoqueVinculos
